/**
 * useSavedMeasurements — Iter 145 snap-target source.
 *
 * Reads /sheets/{sheetId}/measurements and exposes the rows so the
 * viewer can feed them into the CAD snap engine. Re-fetches when the
 * external reloadKey bumps (this is the same trigger MeasurementsLayer
 * uses, so the two stay in sync without a separate event channel).
 *
 * GH-13 (real-plan certification, 2026-09-30) — a failed read used to
 * reset `rows` to `[]`, indistinguishable from "this sheet genuinely
 * has no saved measurements yet". A transient network blip could make
 * real, saved field measurements silently vanish from the snap engine
 * (and anything else trusting this hook) with no indication anything
 * went wrong. Now a failure keeps the LAST CONFIRMED rows for that
 * exact sheet identity and reports `stale: true` instead.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

export function useSavedMeasurements(sheetId, reloadKey) {
  const [state, setState] = useState({ rows: [], error: null, stale: false });
  const lastGoodBySheet = useRef({});

  useEffect(() => {
    if (!sheetId) { setState({ rows: [], error: null, stale: false }); return; }
    let cancel = false;
    api.get(`/sheets/${sheetId}/measurements`)
      .then((r) => {
        if (cancel) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        lastGoodBySheet.current[sheetId] = rows;
        setState({ rows, error: null, stale: false });
      })
      .catch((e) => {
        if (cancel) return;
        setState({
          rows: lastGoodBySheet.current[sheetId] || [],
          error: e?.message || "Failed to load saved measurements",
          stale: true,
        });
      });
    return () => { cancel = true; };
  }, [sheetId, reloadKey]);

  return state;
}
